import { useState, useEffect, useMemo, useCallback } from "react";
import styles from "./FareClasses.module.css";
import AddFareClassModal from "../../../../components/Products/Flights/FareClasses/AddFareClassModal";
import SearchableSelect from "../../../../components/BaseComponents/SearchableSelect/SearchableSelect";
import { Search, Download, RefreshCw, Edit2, Plane, Filter } from "lucide-react";
import {
  useGetFareClasses,
  useCreateFareClass,
  useUpdateFareClass,
  useDeleteFareClass,
} from "../../../../api/products/useFareClassesApi";
import { useGetAirlines } from "../../../../api/products/useAirlinesApi";
import { useGetCabinClasses } from "../../../../api/products/useCabinClassesApi";

const FareClasses = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [airlineFilter, setAirlineFilter] = useState("all");
  const [cabinFilter, setCabinFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFareClass, setEditingFareClass] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [airlineSearch, setAirlineSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: fareClasses = [], pagination, loading, error, refetch } = useGetFareClasses({
    page: 1,
    limit: 500,
    search: debouncedSearch,
    airline: airlineFilter === "all" ? "" : airlineFilter,
    cabinClass: cabinFilter === "all" ? "" : cabinFilter,
    status: statusFilter,
  });

  const { data: airlines = [] } = useGetAirlines({ page: 1, limit: 100, status: "", search: airlineSearch });
  const { data: cabinClasses = [] } = useGetCabinClasses({ page: 1, limit: 100, status: "" });

  const airlineOptions = useMemo(() => [
    { value: "all", label: "All Airlines" },
    ...airlines.map((a) => ({ value: a.id, label: `${a.airlineName} (${a.iataCode})` })),
  ], [airlines]);

  const handleAirlineSearch = useCallback((q) => setAirlineSearch(q.trim()), []);

  const { execute: createFareClass } = useCreateFareClass();
  const { execute: updateFareClass } = useUpdateFareClass();
  const { execute: deleteFareClass } = useDeleteFareClass();

  const handleEdit = (fareClass) => {
    setEditingFareClass(fareClass);
    setApiError(null);
    setShowAddModal(true);
  };

  const handleDelete = async (fareClassId) => {
    if (window.confirm("Are you sure you want to delete this fare family?")) {
      const result = await deleteFareClass(fareClassId);
      if (result.success) {
        refetch();
      } else {
        alert(result.message || "Failed to delete fare family");
      }
    }
  };

  const handleSaveFareClass = async (fareClassData) => {
    setApiError(null);
    if (editingFareClass) {
      const result = await updateFareClass(editingFareClass.id, fareClassData);
      if (result.success) {
        refetch();
        handleCloseModal();
        return;
      }
      setApiError(result.message);
      return result.message;
    }
    const result = await createFareClass(fareClassData);
    if (result.success) {
      refetch();
      handleCloseModal();
      return;
    }
    setApiError(result.message);
    return result.message;
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingFareClass(null);
    setApiError(null);
  };

  const handleRefresh = () => refetch();

  const handleExport = () => {
    console.log("EXPORT FARE FAMILIES DATA");
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setAirlineFilter("all");
    setCabinFilter("all");
    setSearchInput("");
  };

  const activeFiltersCount =
    (statusFilter !== "all" ? 1 : 0) +
    (airlineFilter !== "all" ? 1 : 0) +
    (cabinFilter !== "all" ? 1 : 0);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Fare Families</h1>
          <p className={styles.subtitle}>
            Manage airline fare families for flight identification and booking. Each fare family is linked to a specific airline, cabin class, flight scope, and fare type.
          </p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          + Add Fare Family
        </button>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>ℹ️</div>
        <div className={styles.infoText}>
          <strong>Business Rules:</strong> Each fare family is linked to exactly one airline and one cabin class. Flight scope and fare type are required. Online check-in settings allow you to specify check-in instructions and URLs.
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={styles.infoBanner} style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
          <div className={styles.infoIcon}>⚠️</div>
          <div className={styles.infoText}>
            {error}
            <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by fare family name, code, or airline..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className={styles.filters}>
          <SearchableSelect
            value={airlineFilter}
            onChange={(val) => setAirlineFilter(val)}
            options={airlineOptions}
            onSearch={handleAirlineSearch}
            placeholder="All Airlines"
            searchPlaceholder="Search airlines..."
            width="200px"
          />

          <select
            value={cabinFilter}
            onChange={(e) => setCabinFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Cabin Classes</option>
            {cabinClasses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.cabinClassName}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {activeFiltersCount > 0 && (
            <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
              <Filter size={16} />
              Clear ({activeFiltersCount})
            </button>
          )}

          <button className={styles.iconBtn} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={18} />
          </button>

          <button className={styles.iconBtn} onClick={handleExport} title="Export">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className={styles.resultsCount}>
        Showing {fareClasses.length} of {pagination?.total ?? fareClasses.length} fare families
        {activeFiltersCount > 0 && <span className={styles.filterIndicator}> (filtered)</span>}
      </div>

      {/* Fare Families Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>CODE</th>
              <th>FARE FAMILY NAME</th>
              <th>AIRLINE</th>
              <th>CABIN CLASS</th>
              <th>FLIGHT SCOPE</th>
              <th>FARE TYPE</th>
              <th>WEB CHECK-IN</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <p>Loading fare families...</p>
                  </div>
                </td>
              </tr>
            ) : fareClasses.length === 0 ? (
              <tr>
                <td colSpan="9" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>🎫</div>
                    <p>No fare families found</p>
                    <span>Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              fareClasses.map((fareClass) => (
                <tr key={fareClass.id}>
                  <td>
                    <div className={styles.fareCodeBadge}>{fareClass.fareClassCode}</div>
                  </td>
                  <td>
                    <strong className={styles.fareName}>{fareClass.fareClassName}</strong>
                  </td>
                  <td>
                    <div className={styles.airlineCell}>
                      <div className={styles.airlineIcon}>
                        <Plane size={16} />
                      </div>
                      <div className={styles.airlineInfo}>
                        <span className={styles.airlineName}>{fareClass.airline}</span>
                        <span className={styles.airlineCode}>{fareClass.airlineCode}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={styles.cabinTag}>{fareClass.cabinClass}</span>
                  </td>
                  <td>
                    <span className={styles.scopeTag}>{fareClass.flightScope || "—"}</span>
                  </td>
                  <td>
                    <span className={styles.typeTag}>{fareClass.fareType || "—"}</span>
                  </td>
                  <td>
                    {fareClass.onlineCheckinRequired ? (
                      <span className={styles.checkinBadgeYes}>Required</span>
                    ) : (
                      <span className={styles.checkinBadgeNo}>No</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        fareClass.status === "active" ? styles.statusActive : styles.statusInactive
                      }`}
                    >
                      {fareClass.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.editBtn}
                        onClick={() => handleEdit(fareClass)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <AddFareClassModal
        open={showAddModal}
        onClose={handleCloseModal}
        onSave={handleSaveFareClass}
        fareClass={editingFareClass}
        cabinClasses={cabinClasses}
        apiError={apiError}
      />
    </div>
  );
};

export default FareClasses;
